import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import authService from '../services/authService';

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

const LoginRegister = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get('tab') === 'register' ? 'register' : 'login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [shopName, setShopName] = useState('');
  // Honeypot field — hidden from real users, bots will fill it
  const [website, setWebsite] = useState('');
  // Turnstile
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileRef = useRef(null);
  const turnstileWidgetId = useRef(null);
  // Email verification
  const [showVerificationScreen, setShowVerificationScreen] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState('');

  const sessionExpired = searchParams.get('expired') === '1';
  const emailVerified = searchParams.get('verified') === '1';

  const { login, register, isUserAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Clear the expired/verified param after showing the banner
  useEffect(() => {
    if (sessionExpired || emailVerified) {
      const timeout = setTimeout(() => {
        setSearchParams({}, { replace: true });
      }, 5000);
      return () => clearTimeout(timeout);
    }
  }, [sessionExpired, emailVerified, setSearchParams]);

  // Redirect if already logged in
  useEffect(() => {
    if (isUserAuthenticated) {
      const from = location.state?.from?.pathname || '/?tab=overview';
      navigate(from, { replace: true });
    }
  }, [isUserAuthenticated, navigate, location]);

  // Initialize Turnstile widget
  const renderTurnstile = useCallback(() => {
    if (!TURNSTILE_SITE_KEY || !turnstileRef.current || !window.turnstile) return;

    // Clean up existing widget
    if (turnstileWidgetId.current !== null) {
      try {
        window.turnstile.remove(turnstileWidgetId.current);
      } catch {
        // Widget may already be removed
      }
      turnstileWidgetId.current = null;
    }

    turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
      sitekey: TURNSTILE_SITE_KEY,
      callback: token => setTurnstileToken(token),
      'expired-callback': () => setTurnstileToken(null),
      'error-callback': () => setTurnstileToken(null),
      theme: 'light',
      size: 'flexible',
    });
  }, []);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;

    // Wait for Turnstile script to load
    const checkAndRender = () => {
      if (window.turnstile) {
        renderTurnstile();
      } else {
        // Retry after script loads
        const interval = setInterval(() => {
          if (window.turnstile) {
            clearInterval(interval);
            renderTurnstile();
          }
        }, 200);
        return () => clearInterval(interval);
      }
    };

    const cleanup = checkAndRender();
    return () => {
      if (cleanup) cleanup();
      if (turnstileWidgetId.current !== null && window.turnstile) {
        try {
          window.turnstile.remove(turnstileWidgetId.current);
        } catch {
          // ignore
        }
      }
    };
  }, [tab, renderTurnstile]);

  const resetTurnstile = () => {
    setTurnstileToken(null);
    if (turnstileWidgetId.current !== null && window.turnstile) {
      try {
        window.turnstile.reset(turnstileWidgetId.current);
      } catch {
        // ignore
      }
    }
  };

  const validate = () => {
    if (!email || !password) {
      setError('Email and password are required.');
      return false;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setError('Please enter a valid email address.');
      return false;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return false;
    }
    if (tab === 'register' && password !== confirmPassword) {
      setError('Passwords do not match.');
      return false;
    }
    // Turnstile check (only if enabled)
    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      setError('Please complete the verification challenge.');
      return false;
    }
    setError('');
    return true;
  };

  const handleResendVerification = async () => {
    setResendLoading(true);
    setResendMessage('');
    const result = await authService.resendVerificationEmail(verificationEmail);
    setResendLoading(false);
    if (result.success) {
      setResendMessage('Verification email sent! Check your inbox.');
    } else {
      setResendMessage('Failed to send. Please try again later.');
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setError('');

    try {
      if (tab === 'login') {
        const result = await login(email, password, turnstileToken);
        if (result.success) {
          navigate('/?tab=overview', { replace: true });
        } else if (result.errorCode === 'AUTH.EMAIL_NOT_VERIFIED') {
          setVerificationEmail(result.email || email);
          setShowVerificationScreen(true);
        } else {
          setError(result.error);
          resetTurnstile();
        }
      } else {
        const registrationData = {
          email,
          password,
          shop_name: shopName,
          website, // honeypot
          cf_turnstile_response: turnstileToken,
        };
        const result = await register(registrationData);
        if (result.success) {
          if (result.emailVerificationRequired) {
            setVerificationEmail(result.email || email);
            setShowVerificationScreen(true);
          } else {
            navigate('/?tab=overview', { replace: true });
          }
        } else {
          setError(result.error);
          resetTurnstile();
        }
      }
    } catch (err) {
      setError('An unexpected error occurred.');
      resetTurnstile();
    }
    setLoading(false);
  };

  // Email verification screen
  if (showVerificationScreen) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="bg-white/95 backdrop-blur-xs rounded-xl shadow-xl border border-lavender-200 p-6 sm:p-8 w-full max-w-md text-center">
          <div className="w-16 h-16 bg-lavender-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-lavender-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-2">Check Your Email</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            We've sent a verification link to <strong>{verificationEmail}</strong>. Please check your inbox and click
            the link to verify your account.
          </p>
          <button
            onClick={handleResendVerification}
            disabled={resendLoading}
            className="text-lavender-600 hover:text-lavender-700 font-medium text-sm underline"
          >
            {resendLoading ? 'Sending...' : "Didn't receive it? Resend verification email"}
          </button>
          {resendMessage && <p className="mt-3 text-sm text-sage-600">{resendMessage}</p>}
          <div className="mt-6">
            <button
              onClick={() => {
                setShowVerificationScreen(false);
                setTab('login');
              }}
              className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            >
              Back to login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="bg-white/95 backdrop-blur-xs rounded-xl shadow-xl border border-lavender-200 p-6 sm:p-8 w-full max-w-md">
        <button
          onClick={() => navigate('/')}
          className="text-sm text-lavender-600 hover:text-lavender-700 font-medium mb-4 flex items-center gap-1"
        >
          &larr; Back to Copilot Printer
        </button>
        {sessionExpired && (
          <div
            role="alert"
            className="bg-amber-50 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-800 text-amber-800 dark:text-amber-200 px-4 py-3 rounded-lg mb-4 text-sm"
          >
            Your session has expired. Please log in again.
          </div>
        )}
        {emailVerified && (
          <div
            role="alert"
            className="bg-green-50 dark:bg-green-900/30 border border-green-300 dark:border-green-800 text-green-800 dark:text-green-200 px-4 py-3 rounded-lg mb-4 text-sm"
          >
            Email verified successfully! You can now log in.
          </div>
        )}
        <div className="flex mb-6">
          <button
            className={`flex-1 py-2 sm:py-3 font-semibold rounded-l-lg transition-colors text-sm sm:text-base ${tab === 'login' ? 'bg-gradient-to-r from-lavender-500 to-lavender-600 text-white' : 'bg-sage-100 text-sage-700'}`}
            onClick={() => {
              setTab('login');
              setError('');
            }}
          >
            Login
          </button>
          <button
            className={`flex-1 py-2 sm:py-3 font-semibold rounded-r-lg transition-colors text-sm sm:text-base ${tab === 'register' ? 'bg-gradient-to-r from-lavender-500 to-lavender-600 text-white' : 'bg-sage-100 text-sage-700'}`}
            onClick={() => {
              setTab('register');
              setError('');
            }}
          >
            Register
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="login-email"
              className="block text-gray-700 dark:text-gray-300 font-medium mb-1 text-sm sm:text-base"
            >
              Email
            </label>
            <input
              id="login-email"
              type="email"
              className="w-full px-3 sm:px-4 py-2 border border-sage-300 rounded-lg focus:ring-2 focus:ring-lavender-500 focus:border-lavender-500 text-sm sm:text-base"
              value={email}
              onChange={e => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
          </div>
          <div>
            <label
              htmlFor="login-password"
              className="block text-gray-700 dark:text-gray-300 font-medium mb-1 text-sm sm:text-base"
            >
              Password
            </label>
            <div className="relative">
              <input
                id="login-password"
                type={showPassword ? 'text' : 'password'}
                className="w-full px-3 sm:px-4 py-2 border border-sage-300 rounded-lg focus:ring-2 focus:ring-lavender-500 focus:border-lavender-500 text-sm sm:text-base"
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoComplete={tab === 'login' ? 'current-password' : 'new-password'}
                required
              />
              <button
                type="button"
                className="absolute right-3 top-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400"
                onClick={() => setShowPassword(v => !v)}
                tabIndex={-1}
              >
                {showPassword ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13.875 18.825A10.05 10.05 0 0112 19c-5.523 0-10-4.477-10-10 0-1.657.403-3.221 1.125-4.575M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0zm2.121 2.121A9.969 9.969 0 0021 12c0-5.523-4.477-10-10-10S1 6.477 1 12c0 1.657.403 3.221 1.125-4.575M4.222 4.222l15.556 15.556"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>
          {tab === 'register' && (
            <>
              <div>
                <label
                  htmlFor="register-confirm-password"
                  className="block text-gray-700 dark:text-gray-300 font-medium mb-1 text-sm sm:text-base"
                >
                  Confirm Password
                </label>
                <input
                  id="register-confirm-password"
                  type={showPassword ? 'text' : 'password'}
                  className="w-full px-3 sm:px-4 py-2 border border-sage-300 rounded-lg focus:ring-2 focus:ring-lavender-500 focus:border-lavender-500 text-sm sm:text-base"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="shop-name"
                  className="block text-gray-700 dark:text-gray-300 font-medium mb-1 text-sm sm:text-base"
                >
                  Shop Name
                </label>
                <input
                  id="shop-name"
                  type="text"
                  value={shopName}
                  onChange={e => setShopName(e.target.value)}
                  required
                  className="w-full px-3 sm:px-4 py-2 border border-sage-300 rounded-lg focus:ring-2 focus:ring-lavender-500 focus:border-lavender-500 text-sm sm:text-base"
                  placeholder="Enter your shop name"
                />
              </div>

              {/* Honeypot field — hidden from real users */}
              <div
                style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }}
                aria-hidden="true"
              >
                <label htmlFor="website">Website</label>
                <input
                  id="website"
                  type="text"
                  name="website"
                  value={website}
                  onChange={e => setWebsite(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>
            </>
          )}

          {/* Cloudflare Turnstile widget */}
          {TURNSTILE_SITE_KEY && <div ref={turnstileRef} className="flex justify-center" />}

          {error && (
            <div
              role="alert"
              aria-live="assertive"
              className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-4 py-2 rounded-lg text-sm"
            >
              {error}
            </div>
          )}
          <button
            type="submit"
            className="w-full py-2 sm:py-3 bg-gradient-to-r from-lavender-500 to-lavender-600 text-white font-semibold rounded-lg hover:from-lavender-600 hover:to-lavender-700 transition-all duration-200 flex items-center justify-center text-sm sm:text-base shadow-xs hover:shadow-md"
            disabled={loading}
          >
            {loading ? (
              <span className="flex items-center">
                <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>Loading...
              </span>
            ) : tab === 'login' ? (
              'Login'
            ) : (
              'Register'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default LoginRegister;
